import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SettingsLayout from "@/components/SettingsLayout";
import api, { formatApiError } from "@/lib/api";
import { sanitizeHtml } from "@/lib/safeHtml";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Bell, Workflow, Mailbox, Plus, Trash2, Mail, MessageSquare, Hash, Briefcase, X,
  Send, Loader2, CheckCircle2, XCircle, MinusCircle,
} from "lucide-react";

/**
 * v1.2 PR-C — Notifications (consolidated).
 *
 * Folds the previously-separate `/settings/notification-rules` and
 * `/settings/email-templates` pages into a single tab with two anchored
 * sections (#rules, #templates). Sticky sub-nav at the top of the page
 * highlights the active section and supports keyboard nav.
 *
 * Legacy URLs:
 *   /settings/notification-rules  → /settings/notifications#rules
 *   /settings/email-templates     → /settings/notifications#templates
 *
 * (Redirects live in App.js; this component handles the hash scroll-into-view
 * via useScrollToHash.)
 */

const SECTIONS = [
  { id: "rules",     label: "Routing rules",  icon: Workflow },
  { id: "templates", label: "Email templates", icon: Mailbox  },
];

function useScrollToHash() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    // wait for layout, then scroll.
    const t = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 50);
    return () => window.clearTimeout(t);
  }, [hash]);
}

export default function SettingsNotifications() {
  useScrollToHash();
  return (
    <SettingsLayout>
      <div className="space-y-6" data-testid="settings-notifications-page">
        <div>
          <div className="eb-overline mb-1">Module 6 · Notifications</div>
          <h1 className="text-3xl font-semibold text-navy-900 dark:text-white flex items-center gap-2" data-testid="notifications-heading">
            <Bell className="w-7 h-7 text-orange-500" /> Notifications
          </h1>
          <p className="text-sm text-navy-500 dark:text-navy-300 mt-1 max-w-2xl">
            Decide which channels fire for which visits, and customise the email copy
            sent to visitors, hosts, and admins.
          </p>
        </div>
        <SectionSubnav sections={SECTIONS} />
        <section id="rules" className="scroll-mt-24" data-testid="section-rules">
          <RulesSection />
        </section>
        <div className="h-px bg-navy-100 dark:bg-navy-700" />
        <section id="templates" className="scroll-mt-24" data-testid="section-templates">
          <TemplatesSection />
        </section>
      </div>
    </SettingsLayout>
  );
}

function SectionSubnav({ sections }) {
  const { hash } = useLocation();
  const nav = useNavigate();
  const active = (hash || `#${sections[0].id}`).replace(/^#/, "");
  return (
    <nav className="sticky top-16 z-10 flex flex-wrap gap-1.5 bg-surface/80 dark:bg-navy-900/80 backdrop-blur-sm py-2 -mx-1 px-1 rounded-lg"
         data-testid="section-subnav">
      {sections.map((s) => {
        const Icon = s.icon;
        const isActive = active === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => nav(`#${s.id}`, { replace: true })}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
              isActive
                ? "bg-orange-500 text-white border-orange-500"
                : "bg-white dark:bg-navy-800 text-navy-700 dark:text-navy-200 border-navy-200 dark:border-navy-700 hover:border-orange-300"
            }`}
            data-testid={`subnav-${s.id}`}
          >
            <Icon className="w-3.5 h-3.5" /> {s.label}
          </button>
        );
      })}
    </nav>
  );
}

// ============================================================================
// Rules section — verbatim port of SettingsNotificationRules body.
// ============================================================================

const ALL_CHANNELS = [
  { k: "email", label: "Email", icon: Mail },
  { k: "in_app", label: "In-app", icon: Bell },
  { k: "sms", label: "SMS", icon: MessageSquare },
  { k: "slack", label: "Slack", icon: Hash },
  { k: "teams", label: "Teams", icon: Briefcase },
];
const DAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

function RulesSection() {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [sites, setSites] = useState([]);
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null);

  // v1.2 PR-C.2 — per-rule dry-run state. Keyed by rule_id.
  //   loading: { [rule_id]: bool }   → spinner on the button
  //   results: { [rule_id]: dryRunResult|{error,retry_after?} }
  // We keep both as separate maps so a stale result doesn't suppress a new
  // spinner.
  const [dryRunLoading, setDryRunLoading] = useState({});
  const [dryRunResults, setDryRunResults] = useState({});

  const isAdmin = ["site_manager", "org_admin", "super_admin"].includes(user?.role);

  const load = async () => {
    try {
      const { data } = await api.get("/notification-rules");
      setRules(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    load();
    if (isAdmin) {
      api.get("/users").then((r) => setHosts((r.data || []).filter((u) => u.role === "host"))).catch(() => {});
    }
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
    api.get("/visitor-types").then((r) => setTypes((r.data || []).filter((x) => x.active !== false))).catch(() => {});
  }, [isAdmin]);

  const remove = async (rid) => {
    if (!window.confirm("Delete this rule?")) return;
    try { await api.delete(`/notification-rules/${rid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const runDryRun = async (rule) => {
    const rid = rule.id;
    setDryRunLoading((s) => ({ ...s, [rid]: true }));
    setDryRunResults((s) => { const n = { ...s }; delete n[rid]; return n; });
    try {
      const { data } = await api.post(`/notification-rules/${rid}/dry-run`);
      setDryRunResults((s) => ({ ...s, [rid]: data }));
      if (data.ok) {
        toast.success("Test notification sent — check your inbox.");
      } else {
        toast.error("Test fired with one or more failures — see inline details.");
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let result;
      if (status === 429) {
        const retry = (detail?.retry_after_seconds ?? 0);
        const mins = Math.max(1, Math.ceil(retry / 60));
        result = { error: "rate_limited", retry_after_minutes: mins, message: detail?.message || `Test limit reached — try again in ${mins} minutes.` };
      } else if (status === 422) {
        result = { error: detail?.error || "invalid", message: detail?.message || "Cannot test this rule." };
      } else if (status === 404) {
        result = { error: "not_found", message: "Rule not found." };
      } else if (status === 403) {
        result = { error: "forbidden", message: "You do not have permission to test this rule." };
      } else {
        result = { error: "unknown", message: formatApiError(e) };
      }
      setDryRunResults((s) => ({ ...s, [rid]: result }));
    } finally {
      setDryRunLoading((s) => { const n = { ...s }; delete n[rid]; return n; });
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="text-xl font-semibold text-navy-900 dark:text-white flex items-center gap-2" data-testid="rules-heading">
            <Workflow className="w-5 h-5 text-orange-500" /> Routing rules
          </h2>
          <p className="text-sm text-navy-500 dark:text-navy-300 mt-1">Decide which channels fire for which visits. Higher priority wins.</p>
        </div>
        <button
          onClick={() => setEditing({ host_id: user?.role === "host" ? user.id : null, channels: ["email", "in_app"], priority: 10, conditions: {}, active: true })}
          className="btn-accent" data-testid="new-rule-btn"
        >
          <Plus className="w-4 h-4" /> New rule
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="eb-card p-10 text-center" data-testid="rules-empty">
          <Workflow className="w-10 h-10 mx-auto text-navy-300 mb-3" />
          <h3 className="font-semibold text-navy-900 dark:text-white">No custom rules</h3>
          <p className="text-sm text-navy-500 dark:text-navy-300 mt-1">Defaults: email + in-app to the host on every arrival.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((r) => {
            const hasChannels = (r.channels || []).length > 0;
            const isAdminTester = isAdmin && r.active && hasChannels;
            const isLoading = !!dryRunLoading[r.id];
            const result = dryRunResults[r.id];
            return (
            <div key={r.id} className="eb-card p-5" data-testid={`rule-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold uppercase tracking-wider text-navy-500 dark:text-navy-300">Priority {r.priority}</span>
                  {!r.host_id && <span className="text-[10px] uppercase tracking-wider text-orange-600 font-bold">Org default</span>}
                  {r.host_id && <span className="text-xs text-navy-700 dark:text-navy-200">Host: <code>{r.host_id.slice(0, 8)}…</code></span>}
                  {!r.active && <span className="text-[10px] uppercase text-danger font-bold">Disabled</span>}
                </div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {r.channels.map((c) => {
                    const meta = ALL_CHANNELS.find((x) => x.k === c);
                    if (!meta) return null;
                    const Ic = meta.icon;
                    return (
                      <span key={c} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-navy-50 dark:bg-navy-700 text-navy-700 dark:text-navy-100">
                        <Ic className="w-3 h-3" /> {meta.label}
                      </span>
                    );
                  })}
                </div>
                <div className="mt-2 text-xs text-navy-500 dark:text-navy-300">
                  {r.conditions?.visitor_type?.length > 0 && <span>Types: {r.conditions.visitor_type.join(", ")} · </span>}
                  {r.conditions?.site_id && <span>Site: <code>{r.conditions.site_id.slice(0, 8)}…</code> · </span>}
                  {r.conditions?.days_of_week?.length > 0 && <span>Days: {r.conditions.days_of_week.join("/")} · </span>}
                  {r.conditions?.time_of_day?.from && <span>{r.conditions.time_of_day.from}–{r.conditions.time_of_day.to}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <button
                    onClick={() => runDryRun(r)}
                    disabled={!isAdminTester || isLoading}
                    title={
                      !hasChannels
                        ? "Enable at least one channel to test"
                        : !r.active
                        ? "Enable the rule to test it"
                        : "Send a test notification to your own channels"
                    }
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md border transition-colors ${
                      isAdminTester
                        ? "bg-white dark:bg-navy-800 text-navy-700 dark:text-navy-100 border-navy-200 dark:border-navy-700 hover:border-orange-300 hover:text-orange-600"
                        : "bg-navy-50 dark:bg-navy-700 text-navy-300 dark:text-navy-500 border-navy-100 dark:border-navy-700 cursor-not-allowed"
                    }`}
                    data-testid={`rule-dry-run-btn-${r.id}`}
                  >
                    {isLoading ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
                    ) : (
                      <><Send className="w-3.5 h-3.5" /> Send test notification</>
                    )}
                  </button>
                )}
                <button onClick={() => setEditing(r)} className="btn-outline text-xs" data-testid={`edit-rule-${r.id}`}>Edit</button>
                <button onClick={() => remove(r.id)} className="btn-ghost text-danger text-xs" data-testid={`del-rule-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              </div>
              {result && (
                <DryRunResultStrip ruleId={r.id} result={result} />
              )}
            </div>
            );
          })}
        </div>
      )}

      {editing && (
        <RuleEditor
          rule={editing}
          hosts={hosts}
          sites={sites}
          types={types}
          isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function RuleEditor({ rule, hosts, sites, types, isAdmin, onClose, onSaved }) {
  const [r, setR] = useState({ ...rule, conditions: rule.conditions || {} });
  const [saving, setSaving] = useState(false);

  const toggleChannel = (k) => {
    setR((s) => ({ ...s, channels: s.channels.includes(k) ? s.channels.filter((c) => c !== k) : [...s.channels, k] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        host_id: r.host_id || null,
        channels: r.channels,
        conditions: r.conditions,
        priority: Number(r.priority) || 10,
        active: r.active !== false,
      };
      if (r.id) {
        await api.patch(`/notification-rules/${r.id}`, payload);
      } else {
        await api.post("/notification-rules", payload);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const setCond = (k, v) => setR((s) => ({ ...s, conditions: { ...s.conditions, [k]: v } }));
  const toggleDay = (d) => {
    const arr = r.conditions?.days_of_week || [];
    setCond("days_of_week", arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d]);
  };
  const toggleType = (t) => {
    const arr = r.conditions?.visitor_type || [];
    setCond("visitor_type", arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]);
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy-900/40 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="rule-editor">
      <div className="w-full max-w-lg bg-white dark:bg-navy-800 h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white dark:bg-navy-800 border-b border-navy-100 dark:border-navy-700 p-5 flex items-center justify-between">
          <div className="font-semibold text-lg text-navy-900 dark:text-white">{r.id ? "Edit rule" : "New rule"}</div>
          <button onClick={onClose} className="btn-ghost"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-5">
          {isAdmin && (
            <div>
              <label className="eb-label">Applies to</label>
              <select value={r.host_id || ""} onChange={(e) => setR({ ...r, host_id: e.target.value || null })} className="eb-input" data-testid="rule-host">
                <option value="">Org default (when host has no specific rule)</option>
                {hosts.map((h) => <option key={h.id} value={h.id}>{h.name || h.email}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="eb-label">Channels</label>
            <div className="flex gap-2 flex-wrap">
              {ALL_CHANNELS.map(({ k, label, icon: I }) => (
                <button type="button" key={k} onClick={() => toggleChannel(k)}
                  className={`px-3 py-1.5 rounded-md text-xs font-bold border inline-flex items-center gap-1.5 ${r.channels.includes(k) ? "bg-orange-500 text-white border-orange-500" : "bg-white dark:bg-navy-800 text-navy-700 dark:text-navy-100 border-navy-200 dark:border-navy-700"}`}
                  data-testid={`rule-channel-${k}`}
                ><I className="w-3.5 h-3.5" /> {label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="eb-label">Priority (higher wins)</label>
            <input type="number" min={0} max={1000} className="eb-input" value={r.priority} onChange={(e) => setR({ ...r, priority: e.target.value })} data-testid="rule-priority" />
          </div>

          <div className="border-t border-navy-100 dark:border-navy-700 pt-5">
            <div className="font-semibold text-sm mb-3 text-navy-900 dark:text-white">Conditions (all must match)</div>
            <div className="space-y-3">
              <div>
                <label className="eb-label">Visitor types (any)</label>
                <div className="flex gap-2 flex-wrap">
                  {types.map((t) => (
                    <button type="button" key={t.id || t.name} onClick={() => toggleType(t.name)}
                      className={`px-3 py-1 rounded-md text-xs border ${(r.conditions?.visitor_type || []).includes(t.name) ? "bg-navy-900 text-white border-navy-900" : "bg-white dark:bg-navy-800 text-navy-700 dark:text-navy-100 border-navy-200 dark:border-navy-700"}`}
                    >{t.name}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="eb-label">Site (optional)</label>
                <select className="eb-input" value={r.conditions?.site_id || ""} onChange={(e) => setCond("site_id", e.target.value || null)} data-testid="rule-site">
                  <option value="">Any site</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="eb-label">Days of week (any)</label>
                <div className="flex gap-2">
                  {DAYS.map((d) => (
                    <button type="button" key={d} onClick={() => toggleDay(d)}
                      className={`px-3 py-1.5 rounded-md text-xs font-bold border ${(r.conditions?.days_of_week || []).includes(d) ? "bg-orange-500 text-white border-orange-500" : "bg-white dark:bg-navy-800 text-navy-700 dark:text-navy-100 border-navy-200 dark:border-navy-700"}`}
                    >{d}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="eb-label">From</label>
                  <input type="time" className="eb-input" value={r.conditions?.time_of_day?.from || ""} onChange={(e) => setCond("time_of_day", { ...(r.conditions?.time_of_day || {}), from: e.target.value })} />
                </div>
                <div>
                  <label className="eb-label">To</label>
                  <input type="time" className="eb-input" value={r.conditions?.time_of_day?.to || ""} onChange={(e) => setCond("time_of_day", { ...(r.conditions?.time_of_day || {}), to: e.target.value })} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-navy-700 dark:text-navy-200">
                <input type="checkbox" checked={!!r.conditions?.pre_registered_only} onChange={(e) => setCond("pre_registered_only", e.target.checked)} />
                Only pre-registered visits
              </label>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-navy-700 dark:text-navy-200">
            <input type="checkbox" checked={r.active !== false} onChange={(e) => setR({ ...r, active: e.target.checked })} />
            Active
          </label>
        </div>
        <div className="p-5 border-t border-navy-100 dark:border-navy-700 sticky bottom-0 bg-white dark:bg-navy-800 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-accent" data-testid="rule-save-btn">
            {saving ? "Saving…" : "Save rule"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Templates section — verbatim port of SettingsEmailTemplates body.
// ============================================================================

function TemplatesSection() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/email-templates"); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (key) => {
    const existing = (data?.overrides || []).find((o) => o.key === key);
    const sys = data?.system_defaults?.[key];
    setEditing({
      id: existing?.id,
      key,
      subject: existing?.subject || sys?.subject || "",
      body_html: existing?.body_html || sys?.body_html || "",
    });
    setPreview(null);
  };

  const save = async () => {
    try {
      if (editing.id) {
        await api.patch(`/email-templates/${editing.id}`, { subject: editing.subject, body_html: editing.body_html });
      } else {
        await api.post("/email-templates", { key: editing.key, subject: editing.subject, body_html: editing.body_html });
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const previewIt = async () => {
    try {
      const { data: p } = await api.post("/email-templates/preview", { key: editing.key });
      setPreview(p);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="text-navy-500 dark:text-navy-300">Loading email templates…</div>;

  return (
    <div data-testid="email-templates-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="text-xl font-semibold text-navy-900 dark:text-white flex items-center gap-2">
            <Mailbox className="w-5 h-5 text-orange-500" /> Email templates
          </h2>
          <p className="text-sm text-navy-500 dark:text-navy-300 mt-1 max-w-2xl">
            Override system email templates per-org. Available variables:&nbsp;
            <code className="text-xs bg-navy-50 dark:bg-navy-700 dark:text-navy-100 px-1 rounded">
              {"{visitor_name}, {visitor_company}, {site_name}, {host_name}, {purpose}"}
            </code>
          </p>
        </div>
      </div>

      {editing ? (
        <div className="eb-card p-6 space-y-3" data-testid="email-edit-card">
          <div className="text-xs uppercase tracking-wider text-orange-600">{editing.key}</div>
          <div>
            <label className="eb-label">Subject</label>
            <input className="eb-input" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} data-testid="email-edit-subject" />
          </div>
          <div>
            <label className="eb-label">Body (HTML)</label>
            <textarea className="eb-input min-h-[180px] font-mono text-xs" value={editing.body_html} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} data-testid="email-edit-body" />
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-outline" onClick={previewIt} data-testid="email-preview-btn">Preview</button>
            <button className="btn-ghost" onClick={() => { setEditing(null); setPreview(null); }}>Cancel</button>
            <button className="btn-accent" onClick={save} data-testid="email-save-btn">Save</button>
          </div>
          {preview && (
            <div className="mt-4 p-4 border border-navy-200 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-800" data-testid="email-preview-pane">
              <div className="text-xs text-navy-500 dark:text-navy-300 mb-1">Subject</div>
              <div className="font-semibold mb-3 text-navy-900 dark:text-white">{preview.subject}</div>
              <div className="text-xs text-navy-500 dark:text-navy-300 mb-1">Body</div>
              <div className="prose max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview.body_html) }} />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {(data.available_keys || []).map((k) => {
            const has = (data.overrides || []).find((o) => o.key === k);
            return (
              <div key={k} className="eb-card p-4 flex items-center justify-between" data-testid={`email-row-${k}`}>
                <div>
                  <div className="font-semibold text-navy-900 dark:text-white">{k}</div>
                  <div className="text-xs text-navy-500 dark:text-navy-300">{has ? "Custom override active" : "Using system default"}</div>
                </div>
                <button className={has ? "btn-outline" : "btn-accent"} onClick={() => startEdit(k)} data-testid={`email-edit-${k}`}>
                  {has ? "Edit override" : "Customise"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// v1.2 PR-C.2 — Dry-run result strip rendered beneath a rule row.
// ============================================================================

const CHANNEL_LABEL = {
  email: "Email", in_app: "In-app", sms: "SMS", slack: "Slack", teams: "Teams",
};

function DryRunResultStrip({ ruleId, result }) {
  // Error envelope (429 / 422 / 404 / 403 / unknown).
  if (result.error) {
    const danger = result.error === "rate_limited" ? "amber" : "red";
    const tone =
      danger === "amber"
        ? "bg-warning-50 dark:bg-warning-900/20 border-warning-200 dark:border-warning-700/40 text-warning-700 dark:text-warning-200"
        : "bg-danger-50 dark:bg-danger-900/20 border-danger-200 dark:border-danger-700/40 text-danger-700 dark:text-danger-200";
    return (
      <div
        className={`mt-3 border rounded-md px-3 py-2 text-xs font-medium ${tone}`}
        data-testid={`rule-dry-run-result-${ruleId}`}
      >
        <span className="inline-flex items-center gap-1.5">
          <XCircle className="w-3.5 h-3.5" /> {result.message}
        </span>
      </div>
    );
  }
  // Success envelope: fired[] is an array of {channel, status, ...}.
  return (
    <div
      className="mt-3 border border-navy-100 dark:border-navy-700 rounded-md px-3 py-2 bg-navy-50 dark:bg-navy-700/30"
      data-testid={`rule-dry-run-result-${ruleId}`}
    >
      <div className="text-[11px] uppercase tracking-wider font-semibold text-navy-500 dark:text-navy-300 mb-1.5">
        Result · {result.adapter_mode === "mock" ? "adapter mode: mock" : "adapter mode: live"}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(result.fired || []).map((f, i) => {
          const label = CHANNEL_LABEL[f.channel] || f.channel;
          if (f.status === "sent") {
            return (
              <span
                key={i}
                className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-success-50 dark:bg-success-900/30 text-success-700 dark:text-success-200 border border-success-200 dark:border-success-700/40"
                title={`Delivered to ${f.recipient || "(no recipient)"}${f.provider_id ? " · provider " + f.provider_id : ""}`}
                data-testid={`rule-dry-run-leg-${ruleId}-${f.channel}`}
              >
                <CheckCircle2 className="w-3 h-3" /> {label} ✓
              </span>
            );
          }
          if (f.status === "failed") {
            return (
              <span
                key={i}
                className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-danger-50 dark:bg-danger-900/30 text-danger-700 dark:text-danger-200 border border-danger-200 dark:border-danger-700/40"
                title={f.error_summary || "Send failed"}
                data-testid={`rule-dry-run-leg-${ruleId}-${f.channel}`}
              >
                <XCircle className="w-3 h-3" /> {label} ✗
              </span>
            );
          }
          // status starts with "skipped_…"
          return (
            <span
              key={i}
              className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-navy-100 dark:bg-navy-700 text-navy-500 dark:text-navy-300 border border-navy-200 dark:border-navy-600"
              title={f.reason || "Skipped"}
              data-testid={`rule-dry-run-leg-${ruleId}-${f.channel}`}
            >
              <MinusCircle className="w-3 h-3" /> {label} — skipped
            </span>
          );
        })}
      </div>
    </div>
  );
}
